// Estado de instalação do PWA: se dá para chamar o prompt nativo, se o app já
// está rodando instalado e em que plataforma estamos.
//
// O `beforeinstallprompt` é disparado uma única vez, bem cedo — antes de o React
// montar. Por isso o evento é capturado num singleton em nível de módulo, no
// carregamento do bundle: esperar um useEffect perderia o evento e o botão
// "Instalar" nunca apareceria.

import { useEffect, useState } from "react";

export type InstallPlatform = "android" | "ios" | "desktop";

// Evento não-padrão do Chromium; não existe na lib DOM.
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const promptListeners = new Set<() => void>();

function announcePromptChange(): void {
  for (const listener of promptListeners) listener();
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    // Sem preventDefault o Chrome mostra a própria mini-infobar; queremos o
    // convite só no lugar que o usuário escolheu (o botão em Perfil).
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    announcePromptChange();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    announcePromptChange();
  });
}

export function detectPlatform(userAgent: string, maxTouchPoints = 0): InstallPlatform {
  if (/iPhone|iPad|iPod/i.test(userAgent)) return "ios";
  // iPadOS 13+ manda user agent de "Macintosh" por padrão (modo desktop), então
  // o iPad cairia em "desktop" e receberia a instrução da barra de endereço, que
  // não existe no Safari do iPad. O que separa um iPad de um Mac é o toque:
  // `maxTouchPoints` é 0 em Mac e 5 em iPad.
  if (/Macintosh/i.test(userAgent) && maxTouchPoints > 1) return "ios";
  if (/Android/i.test(userAgent)) return "android";
  return "desktop";
}

export function isStandaloneDisplay(win: {
  matchMedia?: (query: string) => { matches: boolean };
  // `standalone` é extensão só do Safari no iOS e a lib DOM não a declara. O
  // `userAgent` entra aqui porque um tipo com todas as propriedades opcionais é
  // "weak" para o TypeScript: sem nenhuma propriedade em comum, passar o
  // `Navigator` real seria recusado.
  navigator: { standalone?: boolean; userAgent?: string };
}): boolean {
  if (win.matchMedia?.("(display-mode: standalone)").matches) return true;
  return win.navigator.standalone === true;
}

export function useInstallPrompt() {
  const [canPrompt, setCanPrompt] = useState(() => deferredPrompt !== null);
  const [isStandalone] = useState(() =>
    typeof window === "undefined" ? false : isStandaloneDisplay(window),
  );
  const platform =
    typeof navigator === "undefined"
      ? "desktop"
      : detectPlatform(navigator.userAgent, navigator.maxTouchPoints);

  useEffect(() => {
    const listener = () => setCanPrompt(deferredPrompt !== null);
    promptListeners.add(listener);
    listener(); // o evento pode ter chegado entre o render e o effect
    return () => {
      promptListeners.delete(listener);
    };
  }, []);

  async function promptInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
    const event = deferredPrompt;
    if (!event) return "unavailable";
    await event.prompt();
    const { outcome } = await event.userChoice;
    // O evento é de uso único: depois de consumido o Chrome não o reemite.
    deferredPrompt = null;
    announcePromptChange();
    return outcome;
  }

  return { canPrompt, isStandalone, platform, promptInstall };
}
